// Shows staff the exact member email before it goes out, with the choice to
// send it or make the change quietly.
import { Loader2, Mail, X } from 'lucide-react';
import type { MemberEmail } from '../types';

interface MemberEmailPreviewModalProps {
  title: string;
  emails: MemberEmail[];
  busy: boolean;
  sendLabel: string;
  quietLabel: string;
  quietNote?: string;
  onSend: () => void;
  onQuiet: () => void;
  onCancel: () => void;
}

export default function MemberEmailPreviewModal({
  title, emails, busy, sendLabel, quietLabel, quietNote, onSend, onQuiet, onCancel,
}: MemberEmailPreviewModalProps) {
  const first = emails[0];
  const sameBody = emails.every((email) => email.body === first?.body && email.subject === first?.subject);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-navy flex items-center gap-2">
            <Mail size={18} />
            {title}
          </h2>
          <button type="button" onClick={onCancel} className="p-1.5 pointer-coarse:p-2.5 pointer-coarse:-m-1 rounded-lg hover:bg-gray-100 transition" aria-label="Close">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {emails.length === 0 ? (
            <p className="text-sm text-gray-500">No member has an email address on this ticket, so nothing would be sent.</p>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                This is exactly what {emails.length === 1 ? 'the member gets' : `${emails.length} members get`}.
                {!sameBody && ' Each person only sees their own words, so the emails differ slightly.'}
              </p>
              {(sameBody ? [first] : emails).map((email) => (
                <div key={email.to} className="rounded-xl border border-gray-200 text-sm">
                  <div className="px-4 py-2 border-b border-gray-100 bg-off-white space-y-0.5">
                    <p className="text-xs text-gray-500">
                      To: {sameBody ? emails.map((row) => row.to).join(', ') : email.to}
                    </p>
                    <p className="font-semibold text-navy">{email.subject}</p>
                  </div>
                  <p className="px-4 py-3 whitespace-pre-wrap text-gray-800 break-words">{email.body}</p>
                </div>
              ))}
            </>
          )}
          {quietNote && <p className="text-xs text-gray-500">{quietNote}</p>}
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              disabled={busy || emails.length === 0}
              onClick={onSend}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy-dark transition disabled:opacity-50"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              {sendLabel}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onQuiet}
              className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition disabled:opacity-50"
            >
              {quietLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
